import { Injectable } from '@angular/core';
import { Actions, ofActionCompleted, ofActionDispatched, Store } from '@ngxs/store';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';

import { AuthService } from '@app/core/auth/services/auth.service';
import {
  WsConferenceMessage,
  WsConferenceMessageOperationEnum,
  WsConferenceMessageTypeEnum,
  WsConferenceService,
} from '@app/core/ws/ws-conference.service';
import { WsOnlineStatusService } from '@app/core/ws/ws-online-status.service';

import { ImGroupState, ImGroupStateItem } from './im-group.state';
import { ImGroupStateActions } from './im-group.state.actions';

@Injectable({ providedIn: 'root' })
export class ImGroupStoreService {
  public get groups$(): Observable<ImGroupStateItem[]> {
    if (!this.initialized) {
      this.initialized = true;

      this.store.dispatch(new ImGroupStateActions.Load());
    }

    return this.store.select(ImGroupState.groups);
  }

  public loading$: Observable<boolean>;

  protected loadingSubject = new BehaviorSubject<boolean>(false);

  protected initialized = false;

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly wsConference: WsConferenceService,
    private readonly authService: AuthService,
    private readonly wsOnlineStatus: WsOnlineStatusService,
  ) {
    this.actions$
      .pipe(
        ofActionDispatched(ImGroupStateActions.Load),
        tap(() => this.loadingSubject.next(true)),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofActionCompleted(ImGroupStateActions.Load),
        tap(() => this.loadingSubject.next(false)),
      )
      .subscribe();

    this.wsConference.message$.pipe(tap((event) => this.handleConferenceMessage(event))).subscribe();

    this.wsOnlineStatus.message$
      .pipe(tap((event) => this.store.dispatch(new ImGroupStateActions.HandleOnlineStatusChanged(event))))
      .subscribe();

    this.loading$ = this.loadingSubject.asObservable();
  }

  public reload(): void {
    this.initialized = true;
    this.store.dispatch(new ImGroupStateActions.Load());
  }

  public getGroupObservable(groupId: string | null): Observable<ImGroupStateItem | null> {
    if (!groupId) {
      return of(null);
    }

    return this.groups$.pipe(map((groups) => groups.find((i) => i.groupId === groupId) ?? null));
  }

  protected handleConferenceMessage(message: WsConferenceMessage): void {
    switch (message.type) {
      case WsConferenceMessageTypeEnum.Group:
        switch (message.operation) {
          case WsConferenceMessageOperationEnum.Add:
          case WsConferenceMessageOperationEnum.Mod:
            this.store.dispatch(new ImGroupStateActions.ReloadGroup(message.groupid));
            break;
          case WsConferenceMessageOperationEnum.Del:
            this.store.dispatch(new ImGroupStateActions.DeleteGroup(message.groupid));
            break;
          default:
            console.warn('Unhandled group im event:', message);
        }
        break;
      case WsConferenceMessageTypeEnum.GroupMember:
        switch (message.operation) {
          case WsConferenceMessageOperationEnum.Add:
          case WsConferenceMessageOperationEnum.Mod:
            if (message.member) {
              this.store.dispatch(new ImGroupStateActions.ReloadGroupMember(message.groupid, message.member.memberID));
            } else {
              this.store.dispatch(new ImGroupStateActions.ReloadGroup(message.groupid));
            }
            break;
          case WsConferenceMessageOperationEnum.Del:
            if (message.member?.memberID === this.authService.user?.id) {
              this.store.dispatch(new ImGroupStateActions.DeleteGroup(message.groupid));
            } else if (message.member) {
              this.store.dispatch(
                new ImGroupStateActions.DeleteGroupMember(
                  message.groupid,
                  message.member.memberID,
                  message.member.memberType,
                ),
              );
            } else {
              this.store.dispatch(new ImGroupStateActions.ReloadGroup(message.groupid));
            }
            break;
          default:
            console.warn('Unhandled group-member im event:', message);
        }
        break;
      case WsConferenceMessageTypeEnum.Conference:
        switch (message.operation) {
          case WsConferenceMessageOperationEnum.Add:
            this.store.dispatch(new ImGroupStateActions.LoadConference(message.groupid));
            break;
          case WsConferenceMessageOperationEnum.Mod:
            if (message.confInfo && typeof message.confInfo.mode === 'number') {
              this.store.dispatch(new ImGroupStateActions.UpdateConference(message.groupid, message.confInfo.mode));
            } else {
              this.store.dispatch(new ImGroupStateActions.LoadConference(message.groupid));
            }
            break;
          case WsConferenceMessageOperationEnum.Del:
            this.store.dispatch(new ImGroupStateActions.DeleteConference(message.groupid));
            break;
          default:
            console.warn('Unhandled conference im event:', message);
        }
        break;
      case WsConferenceMessageTypeEnum.Member:
        switch (message.operation) {
          case WsConferenceMessageOperationEnum.Mod:
          case WsConferenceMessageOperationEnum.StartSpeak:
          case WsConferenceMessageOperationEnum.EndSpeak:
          case WsConferenceMessageOperationEnum.Add:
            if (message.member) {
              this.store.dispatch(new ImGroupStateActions.UpsertConferenceMember(message.groupid, message.member));
            } else {
              this.store.dispatch(new ImGroupStateActions.ReloadConferenceMembers(message.groupid));
            }
            break;
          case WsConferenceMessageOperationEnum.Del:
            if (message.member) {
              this.store.dispatch(new ImGroupStateActions.DeleteConferenceMember(message.groupid, message.member));
            } else {
              this.store.dispatch(new ImGroupStateActions.ReloadConferenceMembers(message.groupid));
            }
            break;
          default:
            console.warn('Unhandled member im event:', message);
        }
        break;
      default:
        console.warn('unhandled im event:', message);
    }
  }
}
